import ClientCompetitor from '../../IndustryInsights/ClientCompetitor/ClientCompetitor';
import MarketShareTable from '../../IndustryInsights/MarketShareTable/MarketShareTable';
import KeyFiguresTable from '../../IndustryInsights/KeyFiguresTable/KeyFiguresTable';
import { IndustryInsightsProvider } from '../../IndustryInsights/Context/Context';
import { Flex, Text, Icon, Title, ButtonSelect } from '@upsales/components';
import { useSelf } from 'App/components/hooks/appHooks';
import IndustryInsightsChart from './IndustryInsightsChart';
import BemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';
import './IndustryInsightsCard.scss';
import React, { useState, useEffect } from 'react';
import logError from 'Helpers/logError';
import UserParamResource from 'App/babel/resources/UserParam';

const ECONOMY_CHART_ORDER_PARAM_ID = 234;

type Props = {
	industryData: {
		code: string;
		description: string;
	};
	accountId: number;
	accountName: string;
	references: {
		companyKeyFigures: React.RefObject<HTMLDivElement>;
		companyComparison: React.RefObject<HTMLDivElement>;
	};
	showPlaceholder?: boolean;
	financialInfo?: {
		turnover: number[];
		result: number[];
		years: string[];
		currency: string;
		formattedTurnover: [string, string];
		formattedResult: [string, string];
	};
};

const IndustryInsightsCard: React.FC<Props> = ({
	industryData,
	accountId,
	accountName,
	references,
	showPlaceholder,
	financialInfo
}) => {
	const classes = new BemClass('IndustryInsightsCard');
	const self = useSelf();

	const [chartOrder, setChartOrder] = useState<string>(self?.userParams?.economyChartOrder || 'desc');

	const options = [
		{ value: 'desc', icon: 'arrow-left', tooltip: T('financials.chartOrder.left') },
		{ value: 'asc', icon: 'arrow-right', tooltip: T('financials.chartOrder.right') }
	] as const;

	useEffect(() => {
		const listener = Tools.$rootScope.$on('economyChartOrder.changed', (e: any, newChartOrder: string) => {
			if (newChartOrder && newChartOrder !== chartOrder) {
				setChartOrder(newChartOrder);
			}
		});

		return () => {
			listener();
		};
	}, [chartOrder]);

	const saveChartOrder = async (newChartOrder: string) => {
		try {
			await UserParamResource.save(ECONOMY_CHART_ORDER_PARAM_ID, newChartOrder, {
				skipNotification: true
			});

			if (self) {
				Tools.AppService.setSelf({
					...self,
					userParams: {
						...self.userParams,
						economyChartOrder: newChartOrder
					}
				});
			}

			Tools.$rootScope.$broadcast('economyChartOrder.changed', newChartOrder);
		} catch (e) {
			logError(e, 'Error saving economy chart order');
		}
	};

	const handleChartOrderChange = (newChartOrder: string) => {
		saveChartOrder(newChartOrder);
		setChartOrder(newChartOrder);
	};

	if (showPlaceholder) return null;

	return (
		<IndustryInsightsProvider description={industryData?.description}>
			<div ref={references.companyKeyFigures} className={classes.elem('companyKeyFigures').b()}>
				{financialInfo ? (
					<div className={classes.elem('innerSection').mod('lessGraphSpacing').b()}>
						<Flex className={classes.elem('pendoHeader').b()} justifyContent="space-between">
							<Title bold size="lg">
								{T('financials.companyKeyFigures')}
							</Title>
						</Flex>
						<Flex direction="column" className={classes.elem('financialInformation').b()}>
							<Flex direction="row" alignItems="center" justifyContent="space-between" space="mrl">
								<Title bold size="sm">
									{T('default.companyGrowth')}
								</Title>
								<ButtonSelect value={chartOrder} options={options} onChange={handleChartOrderChange} />
							</Flex>
							<Flex direction="column" alignItems="center">
								<IndustryInsightsChart
									placeholder={false}
									turnover={financialInfo.turnover}
									result={financialInfo.result}
									years={financialInfo.years}
									currency={financialInfo.currency}
									chartOrder={chartOrder}
								/>
								<Flex gap="u4" justifyContent="space-around" className={classes.elem('keyFigures').b()}>
									<Flex alignItems="center" gap="u3">
										<Icon name="circle" color="green" />
										<Flex direction="column" gap="u2">
											<Text size="sm" color="grey-10">
												{T('financials.revenue')} {financialInfo.years[0]}
											</Text>
											<Flex>
												<Text size="lg" bold>
													{financialInfo.formattedTurnover[0]}{' '}
													{financialInfo.formattedTurnover[1]}
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Flex alignItems="center" gap="u3">
										<Flex direction="column" gap="u2">
											<Icon
												className={classes.mod('overlapRedIcon').b()}
												name="circle"
												color="red"
											/>
											<Icon
												className={classes.mod('overlapGreenIcon').b()}
												name="circle"
												color="success-4"
											/>
										</Flex>
										<Flex direction="column" gap="u2">
											<Text size="sm" color="grey-10">
												{T('financials.annualResult', {
													year: financialInfo.years[0]
												})}
											</Text>
											<Flex>
												<Text size="lg" bold>
													{financialInfo.formattedResult[0]}{' '}
													{financialInfo.formattedResult[1]}
												</Text>
											</Flex>
										</Flex>
									</Flex>
								</Flex>
							</Flex>
						</Flex>
					</div>
				) : null}
				{industryData ? (
					<div className={classes.elem('innerSection').mod('lessSpacing').b()}>
						<KeyFiguresTable clientId={accountId} clientName={accountName} code={industryData.code} />
					</div>
				) : null}
			</div>
			{industryData ? (
				<div ref={references.companyComparison} className={classes.elem('companyComparison').b()}>
					<Flex className={classes.elem('innerSection').b()} direction="column">
						<ClientCompetitor clientId={accountId} clientName={accountName} code={industryData.code} />
						<MarketShareTable clientId={accountId} clientName={accountName} code={industryData.code} />
					</Flex>
				</div>
			) : null}
		</IndustryInsightsProvider>
	);
};

export default IndustryInsightsCard;
